import React from 'react';
import { useFormStore } from '../store/formStore';
import { CheckCircle, Circle } from 'lucide-react';

export const ProgressBar: React.FC = () => {
  const { currentStepOverall, totalSteps, currentSection, sections } = useFormStore();
  const progress = totalSteps > 0 ? ((currentStepOverall + 1) / totalSteps) * 100 : 0;

  const sectionKeys = Object.keys(sections);
  const currentSectionIndex = sectionKeys.indexOf(currentSection);

  return (
    <div className="w-full space-y-6">
      {/* Minimal Progress Indicator */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-900">
              Step {currentStepOverall + 1} of {totalSteps}
            </span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
              {Math.round(progress)}% complete
            </span>
          </div>
        </div>

        {/* Subtle Progress Bar */}
        <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-gray-900 to-gray-800 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      </div>

      {/* Section Progress Indicators - Only show if multiple sections */}
      {sectionKeys.length > 1 && (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sections</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {sectionKeys.map((sectionKey, index) => {
            const section = sections[sectionKey];
            const isCompleted = index < currentSectionIndex;
            const isCurrent = index === currentSectionIndex;
            const isUpcoming = index > currentSectionIndex;

            return (
              <div
                key={sectionKey}
                className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg border transition-all duration-200 ${
                  isCompleted
                    ? 'bg-gray-50 border-gray-200'
                    : isCurrent
                    ? 'bg-gray-50 border-gray-200'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex-shrink-0">
                  {isCompleted ? (
                    <div className="w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : isCurrent ? (
                    <div className="w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  ) : (
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate ${
                    isCompleted
                      ? 'text-gray-900'
                      : isCurrent
                      ? 'text-gray-900'
                      : 'text-gray-500'
                  }`}>
                    {section.title}
                  </p>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
